import {
  closestCorners,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import {ReactNode, useEffect, useState} from 'react';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/active-unchecked.svg';
import {ReactComponent as CheckedIndeterminateIcon} from '../../../../src/assets/images/icon-checked-indeterminate.svg';
import {
  useGetCodingFormList,
  useSaveCodingFormSortOrder,
} from '../../../hooks/document/useDocument';
import {ToastType} from '../../../shared/constants';
import ArrButton from '../../common/arr-button/ArrButton';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrTransferList, {
  KeyValue,
} from '../../common/arr-transfer-list/ArrTransferList';
import './SecurityGroupCodingForm.scss';

type SecurityGroupCodingFormProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
  handleOnSuccess: (successMsg: string) => void;
};

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  className?: string;
}

interface ToastMessage {
  error?: string;
  success?: string;
}
interface TabData {
  label: string;
  content: React.ReactNode;
}

const dividerStyle = {
  bgcolor: '#3C4F6B',
  width: '0.6px',
  my: 1,
};

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`security-group-coding-form-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`security-group-coding-form-tab-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

export function SortableItem(
  props: Readonly<{
    id: number;
    formData: KeyValue;
    allFormData: KeyValue[];
    isDisabled: boolean;
  }>
) {
  const {attributes, listeners, setNodeRef, transform, transition} =
    useSortable({id: props.id, disabled: props.isDisabled});
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleProps = {...listeners, ...attributes};
  return (
    <List
      ref={setNodeRef}
      style={style}
      className="list-item security-group-all-coding-form"
    >
      <ListItem className={`list-item-row`}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center">
              <ArrIconButton
                icon={<MenuIcon />}
                onClick={(e) => e.stopPropagation()}
                {...(handleProps || {})}
                ariaLabel="dragdrop"
                shape="default"
                size="small"
                testId={`security-group-coding-form-dragdrop-icon-button-${props.id}`}
              />
              <Typography
                className={`typo-text body3_size box-sub-heading`}
                noWrap
                data-testid={`security-group-coding-form-label-${props.id}`}
              >
                {props.formData.label}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    </List>
  );
}

export const SecurityGroupCodingForm: React.FC<
  SecurityGroupCodingFormProps
> = ({showDrawer, handleDrawer, handleOnSuccess}) => {
  const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(true);
  const [isFormSelection, setIsFormSelection] = useState<boolean>(true);
  const [isAllCodingFormSelected, setIsAllCodingFormSelected] =
    useState<boolean>(false);
  const [isNextButtonClicked, setIsNextButtonClicked] =
    useState<boolean>(false);
  const [value, setValue] = useState<number>(0);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<ToastMessage>();

  const [availableAssignedList, setAvailableAssignedList] = useState<
    KeyValue[]
  >([]);
  const [dragableItems, setdragableItems] = useState<KeyValue[]>([]);
  const {data: getCodingFormList, isLoading: getCodingFormListLoading} =
    useGetCodingFormList();
  const {
    mutate: saveCodingFormSortOrder,
    isPending: saveCodingFormSortOrderLoading,
    data: saveCodingFormSortOrderResponse,
    error: saveCodingFormSortOrderError,
  } = useSaveCodingFormSortOrder();
  const translate = (key: string) => t(`codingForms.securityGroup.${key}`);

  useEffect(() => {
    if (saveCodingFormSortOrderResponse?.message) {
      handleOnSuccess(saveCodingFormSortOrderResponse?.message);
      handleDrawer(false);
    } else if (saveCodingFormSortOrderError) {
      setToastMessage({error: saveCodingFormSortOrderError.message});
      setShowToast(true);
    }
  }, [saveCodingFormSortOrderError, saveCodingFormSortOrderResponse]);

  useEffect(() => {
    if (!getCodingFormListLoading && getCodingFormList?.length) {
      setAvailableAssignedList(
        getCodingFormList?.map((item) => {
          return {
            id: item.codingFormId,
            label: item.codingFormName,
            ...item,
          };
        })
      );
      setdragableItems(
        getCodingFormList
          .filter((item) => item.isSelected)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((item) => {
            return {
              id: item.codingFormId,
              isDefault: item.isDefault,
              label: item.codingFormName,
            };
          })
      );
    }
  }, [getCodingFormList, getCodingFormListLoading]);

  useEffect(() => {
    setIsSubmitDisable(
      !(dragableItems.length && dragableItems.some((x) => x.isDefault))
    );
  }, [dragableItems]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const {active, over} = event;

    if (over && active.id !== over.id) {
      const oldIndex = dragableItems
        .map((item) => item.id)
        .indexOf(Number(active.id));
      const newIndex = dragableItems
        .map((item) => item.id)
        .indexOf(Number(over.id));
      if (oldIndex === 0 || newIndex === 0) {
        return;
      }
      setdragableItems((dragableItems) =>
        arrayMove(dragableItems, oldIndex, newIndex)
      );
    }
  }

  const keepDefaultOnTop = () => {
    const localList = [
      dragableItems.find((item) => item.isDefault) ?? dragableItems[0],
      ...dragableItems.filter((item) => !item.isDefault),
    ];
    setdragableItems(localList);
  };

  const handleAssignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setdragableItems((prev) => {
      if (isChecked) {
        const alreadyExists = prev.some((u) => u.id === user.id);
        return alreadyExists ? prev : [...prev, user];
      } else {
        return prev.filter((u) => u.id !== user.id);
      }
    });
  };

  const onChangeAllCodingForm = () => {
    // select / unselect all coding form name
    setdragableItems(isAllCodingFormSelected ? [] : [...availableAssignedList]);
    setIsAllCodingFormSelected(!isAllCodingFormSelected);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!isMaxCodingFormListExceed()) {
      setValue(newValue);
      if (newValue) {
        // Save
        keepDefaultOnTop();
        setIsFormSelection(false);
      } else {
        setIsFormSelection(true);
      }
    }
  };

  const isMaxCodingFormListExceed = () => {
    if (dragableItems.length > 15) {
      setToastMessage({error: translate('errorDesc')});
      setShowToast(true);
      return true;
    }
    return false;
  };

  const onClickNextSaveBtn = () => {
    if (isFormSelection) {
      if (!isMaxCodingFormListExceed()) {
        keepDefaultOnTop();
        setValue(1);
        setIsFormSelection(false);
        setIsNextButtonClicked(true);
      }
    } else {
      saveCodingFormSortOrder(
        dragableItems.map((item, index) => {
          return {
            codingFormId: item.id,
            isDefault: item.isDefault ?? false,
            sortOrder: index + 1,
          };
        })
      );
    }
  };

  const codingFormSelectionBody = (
    <div data-testid={`security-group-coding-form-div-wrapper`}>
      <List className="list-item security-group-all-coding-form">
        <ListItem className={`list-item-row`}>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                <Checkbox
                  data-testid="security-group-coding-form-select-all-checkbox"
                  checked={isAllCodingFormSelected}
                  onChange={() => onChangeAllCodingForm()}
                  className="list-item-checkbox"
                  icon={<UnCheckedIcon />}
                  checkedIcon={<CheckedIndeterminateIcon />}
                />
                <Divider orientation="vertical" sx={dividerStyle} flexItem />
                <Typography
                  className={`typo-text body3_size box-sub-heading`}
                  noWrap
                  data-testid={`security-group-coding-form-coding-forms`}
                >
                  {translate('codingForms')}
                </Typography>
              </Box>
            }
            data-testid="main-top-checkbox"
          />
        </ListItem>
      </List>

      <ArrTransferList
        showCheckbox={true}
        selectedList={dragableItems}
        displayedList={availableAssignedList}
        onChange={handleAssignChange}
        checkboxDataTestId="security-group-coding-form-transfer-list-checkbox"
        radioSize="xs"
        showRadio={true}
        onRadioBtnClick={(e) => {
          setAvailableAssignedList(
            availableAssignedList.map((item) => {
              return item.id === e.id
                ? {
                    ...item,
                    isDefault: true,
                  }
                : {
                    ...item,
                    isDefault: false,
                  };
            })
          );
          const localSelectedList = dragableItems.map((item) => {
            return item.id === e.id
              ? {
                  ...item,
                  isDefault: true,
                }
              : {
                  ...item,
                  isDefault: false,
                };
          });
          setdragableItems(localSelectedList);
        }}
      />
    </div>
  );

  const codingFormOrderBody = (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
        modifiers={[]}
      >
        <SortableContext items={dragableItems} strategy={rectSortingStrategy}>
          <Typography
            className={`typo-text body3_size coding-form-order-sub-heading`}
            noWrap
            data-testid={`security-group-coding-form-selected-fields`}
          >
            {translate('selectedFields')}
          </Typography>
          {dragableItems.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              formData={item}
              allFormData={dragableItems}
              isDisabled={item.isDefault ?? false}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );

  const tabData: TabData[] = [
    {
      label: translate('codingFormSelection'),
      content: codingFormSelectionBody,
    },
    {
      label: translate('codingFormOrder'),
      content: codingFormOrderBody,
    },
  ];

  const CustomLoadingOverlay: React.FC = () => {
    return (
      <Box
        className={
          saveCodingFormSortOrderLoading || getCodingFormListLoading
            ? 'loading-overlay'
            : ''
        }
      >
        <ArrLoadingSpinner
          show={saveCodingFormSortOrderLoading || getCodingFormListLoading}
        />
      </Box>
    );
  };

  const contentBody = (
    <Box className="main-container" data-testid="main-container">
      <Box className="header-box">
        <Typography
          className="typo-text headline3_size securityGroup-title"
          data-testid={`security-group-coding-form-title`}
        >
          {translate('title')}
        </Typography>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              handleDrawer(false);
            }}
            disabled={false}
            dataTestId="security-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={isSubmitDisable}
            dataTestId="security-group-save"
            form="security-group-form"
            onClick={onClickNextSaveBtn}
          >
            {isFormSelection ? t('common.next') : t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="content-box">
        <div
          className="tab-header"
          data-testid={`security-group-coding-form-tabs-wrapper`}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="assign user tabs"
            className="security-group-tabs"
          >
            {tabData.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                className="security-group-tab"
                disabled={
                  index === 1 && (isSubmitDisable || !isNextButtonClicked)
                }
              />
            ))}
          </Tabs>

          <p
            className="security-group-desc"
            data-testid={`security-group-coding-form-p`}
          >
            {isFormSelection
              ? translate('codingFormSelectionDesc')
              : translate('codingFormOrderDesc')}
          </p>

          {tabData.map((tab, index) => (
            <TabPanel key={tab.label} value={value} index={index}>
              {tab.content}
            </TabPanel>
          ))}
        </div>
      </Box>
      <CustomLoadingOverlay />
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showButton={false}
          showHeader={false}
          isOpen={showDrawer}
          size="large"
          side="right"
          onClose={() => handleDrawer(false)}
        >
          {contentBody}
        </ArrDrawer>
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage?.error ?? toastMessage?.success ?? ''}
          status={toastMessage?.error ? [ToastType.Error] : [ToastType.Success]}
          onClose={() => setShowToast(false)}
          setErrorIconAsAlert
        />
      )}
    </>
  );
};
